import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Flex,
  Heading,
  HStack,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Stack,
  Tag,
  Text,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { useSelector } from "react-redux";

/* -----------------------------
   Helpers: parse start date from strings like:
   "February 18th 2026 - February 20th 2026"
------------------------------ */
const normalize = (s) => (s || "").toLowerCase().trim().replace(/\s+/g, " ");

const toDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const stripOrdinal = (s) => s.replace(/(\d+)(st|nd|rd|th)/gi, "$1");

const parseStartDate = (rangeStr) => {
  if (!rangeStr) return null;
  const left = rangeStr.split("-")[0]?.trim() || rangeStr.trim();
  const cleaned = stripOrdinal(left);
  const d = new Date(cleaned);
  if (Number.isNaN(d.getTime())) return null;
  return toDay(d);
};

export default function NewsCards() {
  const isMobile = useSelector((state) => state.windowSize.isMobile);

  // ✅ Converted dataset (from your original code)
  const newsItems = useMemo(
    () => [
      {
        id: "edi-2026",
        title: "Equity Diversity and Inclusion (EDI) WORKSHOP",
        dateRange: "February 18th 2026 - February 20th 2026",
        location: "#300, 350 Legget Drive, K2K 2W7, Kanata, ON",
        image: "N-EDI en 2026.jpg",
      },
      {
        id: "survey-tutorial-2026",
        title: "TRAVERSAL SURVEY AND TUTORIAL WORKSHOP",
        dateRange: "February 17th 2026 - February 27st 2026",
        location: "#300, 350 Legget Drive, K2K 2W7, Kanata, ON",
        image: "N-SurveyandTutorials_en 2026.jpg",
      },
      {
        id: "hackathon-2025",
        title: "TAVERSAL ANNUAL WORKSHOP and HACKATHON",
        dateRange: "June 2nd 2025 - June 4th 2025",
        location: "#300, 350 Legget Drive, K2K 2W7, Kanata, ON",
        image: "N-hackathon 2025.jpg",
      },
      {
        id: "survey-tutorial-2025",
        title: "CREATE TRAVERSAL TUTORIAL AND SURVEY WORKSHOP",
        dateRange: "February 18th 2025 - February 28th 2025",
        location: "Hybrid - 535 LEGGET DRIVE, SUITE 200, KANATA, ON K2K 3B8",
        image: "./Workshop2025-EN 1.png",
      },
      {
        id: "edi-2024-dec16",
        title: "Equity Diversity and Inclusion (EDI) Workshops",
        dateRange: "December 16th 2024",
        location: "535 LEGGET DRIVE, SUITE 200, KANATA, ON K2K 3B8",
        image: "EDI en.jpg",
      },
      {
        id: "wf-iot-2024",
        title:
          "TRAVERSAL Workshop in conjunction with IEEE World Forum on the Internet of Things",
        dateRange: "November 10th 2024",
        location: "Westin - Ottawa",
        // no image => non-clickable
      },
      {
        id: "hackathon-2024",
        title: "TAVERSAL ANNUAL WORKSHOP and HACKATHON",
        dateRange: "June 20st 2024 - June 22nd 2024",
        location: "535 LEGGET DRIVE, SUITE 200, KANATA, ON K2K 3B8",
        image: "NewsHack.jpg",
      },
      {
        id: "nem-ontario-2024",
        title:
          "NEM-Ontario Event co-located with TRAVERSAL Technology Transfer and Entrepreneurship (TTE) series",
        dateRange: "March 25th 2024 - March 30th 2024",
        location: "535 LEGGET DRIVE, SUITE 200, KANATA, ON K2K 3B8",
        // no image => non-clickable
      },
      {
        id: "survey-2024",
        title: "TRAVERSAL SURVEY AND TUTORIAL Workshop",
        dateRange: "February 20th 2024 - March 1st 2024",
        location: "535 LEGGET DRIVE, SUITE 200, KANATA, ON K2K 3B8",
        image: "NewsSurvey.jpg",
      },
      {
        id: "edi-2023",
        title: "CREATE TRAVERSAL EDI Workshop",
        dateRange: "December 18th 2023 - December 22nd 2023",
        location: "535 LEGGET DRIVE, SUITE 200, KANATA, ON K2K 3B8",
        image: "NewsEDI.jpg",
      },
    ],
    []
  );

  // Theme
  const accent = "#DC242C";
  const titleColor = useColorModeValue("#315369", "white");
  const subColor = useColorModeValue("gray.700", "gray.300");
  const border = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.10)");
  const cardBg = useColorModeValue("white", "rgba(255,255,255,0.04)");
  const panelBg = useColorModeValue("white", "rgba(255,255,255,0.03)");

  // Modal
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedImage, setSelectedImage] = useState(null);

  const openModal = (src) => {
    if (!src) return; // guard for non-clickable cards
    setSelectedImage(src);
    onOpen();
  };

  // Controls
  const [query, setQuery] = useState("");
  const [fromDate, setFromDate] = useState(""); // YYYY-MM-DD
  const [toDate, setToDate] = useState(""); // YYYY-MM-DD
  const [sortMode, setSortMode] = useState("newest"); // newest | oldest | title

  const PAGE_SIZE = isMobile ? 4 : 6;
  const [visible, setVisible] = useState(PAGE_SIZE);

  const filtered = useMemo(() => {
    const q = normalize(query);
    const from = fromDate ? toDay(fromDate) : null;
    const to = toDate ? toDay(toDate) : null;

    let out = [...newsItems].filter((x) => {
      // keyword
      if (q) {
        const hay = normalize([x.title, x.location, x.dateRange].filter(Boolean).join(" "));
        if (!hay.includes(q)) return false;
      }

      // date range filter (using start date)
      const sd = parseStartDate(x.dateRange);
      if ((from || to) && !sd) return false;
      if (from && sd < from) return false;
      if (to && sd > to) return false;

        return true;
      });

      out.sort((a, b) => {
        if (sortMode === "title") return (a.title || "").localeCompare(b.title || "");
        const da = parseStartDate(a.dateRange)?.getTime() ?? 0;
        const db = parseStartDate(b.dateRange)?.getTime() ?? 0;
        return sortMode === "oldest" ? da - db : db - da;
      });

      return out;
    }, [newsItems, query, fromDate, toDate, sortMode]);

    const today = toDay(new Date());

    const isUpcoming = (dateRange) => {
      const sd = parseStartDate(dateRange);
      if (!sd) return false;
      return sd >= today;
    };

  return (
    <Box py={{ base: 8, md: 10 }}>
      <Container maxW="1200px">
        {/* Header */}
        <Stack spacing={2} mb={6}>
          <Heading fontSize={{ base: "xl", md: "2xl" }} color={titleColor} letterSpacing="tight">
            News & Events
          </Heading>
          <Text color={subColor}>
            Workshops, hackathons, and community events from the SITE-CAV ecosystem.
          </Text>
        </Stack>

        {/* Filters */}
        <Box
          border="1px solid"
          borderColor={border}
          bg={panelBg}
          borderRadius="22px"
          p={{ base: 4, md: 5 }}
          mb={6}
        >
          <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4} alignItems="center">
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <SearchIcon color={useColorModeValue("gray.500", "gray.400")} />
              </InputLeftElement>
              <Input
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setVisible(PAGE_SIZE);
                }}
                placeholder="Search title, location, date…"
                borderRadius="14px"
              />
            </InputGroup>

            <Input
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setVisible(PAGE_SIZE);
              }}
              borderRadius="14px"
            />

            <Input
              type="date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setVisible(PAGE_SIZE);
              }}
              borderRadius="14px"
            />

            <Input
              as="select"
              value={sortMode}
              onChange={(e) => {
                setSortMode(e.target.value);
                setVisible(PAGE_SIZE);
              }}
              borderRadius="14px"
            >
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="title">Title (A–Z)</option>
            </Input>
          </SimpleGrid>

          <HStack mt={4} spacing={3} flexWrap="wrap">
            <Tag borderRadius="full" fontWeight="900">
              Showing {Math.min(visible, filtered.length)} / {filtered.length}
            </Tag>

            {query || fromDate || toDate ? (
              <Button
                size="sm"
                variant="ghost"
                fontWeight="900"
                onClick={() => {
                  setQuery("");
                  setFromDate("");
                  setToDate("");
                  setVisible(PAGE_SIZE);
                }}
              >
                Clear filters
              </Button>
            ) : null}
          </HStack>
        </Box>

        {/* Cards */}
        {filtered.length === 0 ? (
          <Box
            border="1px solid"
            borderColor={border}
            borderRadius="22px"
            p={10}
            textAlign="center"
            bg={panelBg}
          >
            <Text fontWeight="900" color={titleColor}>
              No news items match your filters.
            </Text>
            <Text color={subColor} mt={2}>
              Try clearing filters or searching different keywords.
            </Text>
          </Box>
        ) : (
          <>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
              {filtered.slice(0, visible).map((x) => {
                const clickable = Boolean(x.image);

                return (
                  <Box
                    key={x.id}
                    role={clickable ? "button" : undefined}
                    tabIndex={clickable ? 0 : -1}
                    onClick={() => openModal(x.image)}
                    onKeyDown={(e) => {
                      if (!clickable) return;
                      if (e.key === "Enter" || e.key === " ") openModal(x.image);
                    }}
                    bg={cardBg}
                    border="1px solid"
                    borderColor={border}
                    borderRadius="22px"
                    p={{ base: 5, md: 6 }}
                    textAlign="left"
                    boxShadow={useColorModeValue("0 10px 24px rgba(0,0,0,0.07)", "none")}
                    transition="all 180ms ease"
                    _hover={clickable ? { transform: "translateY(-2px)" } : undefined}
                    opacity={clickable ? 1 : 0.9}
                    cursor={clickable ? "pointer" : "default"}
                  >
                    <HStack spacing={2} flexWrap="wrap">
                      <Tag
                        size="sm"
                        borderRadius="full"
                        bg={useColorModeValue("rgba(49,83,105,0.08)", "rgba(255,255,255,0.08)")}
                        fontWeight="900"
                        color={titleColor}
                      >
                        Event
                      </Tag>

                      {isUpcoming(x.dateRange) ? (
                        <Tag size="sm" borderRadius="full" bg={accent} color="white" fontWeight="900">
                          UPCOMING
                        </Tag>
                      ) : null}

                      {!clickable ? (
                        <Tag
                          size="sm"
                          borderRadius="full"
                          bg={useColorModeValue("rgba(0,0,0,0.06)", "rgba(255,255,255,0.10)")}
                          color={subColor}
                          fontWeight="900"
                        >
                          No poster
                        </Tag>
                      ) : null}
                    </HStack>

                    <Text mt={3} fontWeight="900" color={titleColor} fontSize="lg" lineHeight={1.2}>
                      {x.title}
                    </Text>

                    <Text mt={3} color={subColor} fontWeight="800">
                      {x.dateRange}
                    </Text>

                    <Divider my={4} borderColor={border} />

                    <Text color={subColor} fontWeight="700">
                      {x.location}
                    </Text>

                    {clickable ? (
                      <Text
                        mt={4}
                        fontSize="sm"
                        color={useColorModeValue("blue.600", "blue.300")}
                        fontStyle="italic"
                      >
                        Click for more information
                      </Text>
                    ) : null}
                  </Box>
                );
              })}
            </SimpleGrid>

            {visible < filtered.length ? (
              <Flex justify="center" mt={8}>
                <Button
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  bg={accent}
                  color="white"
                  fontWeight="900"
                  borderRadius="16px"
                  _hover={{ filter: "brightness(0.95)" }}
                >
                  Load more
                </Button>
              </Flex>
            ) : null}
          </>
        )}
      </Container>

      {/* Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="3xl" allowPinchZoom>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>About Event</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedImage ? (
              <Image src={selectedImage} alt="Event poster" width="100%" objectFit="contain" />
            ) : null}
          </ModalBody>
          <ModalFooter>
            <Button bg={accent} color="white" fontWeight="900" onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
